/* FOM · Evidence Inspector — a cadeia epistemologica de um objeto, a um clique.

     LACUNA -> PERGUNTA -> EVIDENCIA -> RESPOSTA -> CLASSIFICACAO (com teto) -> CONHECIMENTO

   Tudo aqui vem de dado que ja existe: know/gaps do no (GET /nodes), as
   investigacoes do no (camada PRIVATE, editor+) e o teto por evidencia que o
   BANCO impoe (trigger C-07; a tabela abaixo e a regra medida em FOM-ARQ-0011
   §2.1). O inspetor NAO calcula teto para encerrar nada — so mostra a regra.
   Para viewer, a camada privada e explicada, nunca simulada. */
import type { Api } from '../core/api.ts';
import type { FomNode, Investigation, InvestigationResumo, MemberRole, StatusClass, KnowItem } from '../core/types.ts';
import { COR_ST, ROTULO_ST, DESCRICAO_ST, rotuloTipo} from '../core/types.ts';
import { ehFuturo } from '../core/lentes.ts';
import { el, botao, dataCurta } from './dom.ts';

export type EvidenciaOpcoes = {
  api: Api;
  org: () => string;
  mapa: () => string;
  papel: () => MemberRole;
  aoInvestigarLacuna: (no: FomNode, lacuna: string) => void;
  aoAbrirInvestigacoes: (no: FomNode) => void;
  aoIrParaNo: (externalId: string) => void;
};

/* teto por evidencia registrada — FOM-ARQ-0011 §2.1 (regra do banco, C-07) */
const TETO: { evidencia: string; permite: StatusClass[] }[] = [
  { evidencia: 'nenhuma', permite: ['ND', 'E4', 'TOBE'] },
  { evidencia: 'relato do operador', permite: ['E3', 'ND', 'E4', 'TOBE'] },
  { evidencia: 'tela ou documento parcial', permite: ['E2', 'E3', 'ND', 'E4', 'TOBE'] },
  { evidencia: 'tela ou documento completo', permite: ['E1', 'E2', 'E3', 'ND', 'E4', 'TOBE'] },
];
const TODAS: StatusClass[] = ['E1', 'E2', 'E3', 'E4', 'ND', 'TOBE'];

function chip(st: StatusClass): HTMLElement {
  const c = el('span', 'ev-chip ev-' + st, ROTULO_ST[st]);
  c.style.setProperty('--cor', COR_ST[st]);
  return c;
}

export function montarEvidencia(hostPai: HTMLElement, o: EvidenciaOpcoes) {
  const veu = el('div', 'evidencia-veu');
  veu.hidden = true;
  hostPai.appendChild(veu);
  let noAtual: FomNode | null = null;

  function fechar(): void {
    veu.hidden = true;
    veu.innerHTML = '';
    noAtual = null;
  }
  veu.addEventListener('click', (ev) => { if (ev.target === veu) fechar(); });

  function secao(titulo: string, contagem?: string): HTMLElement {
    const s = el('section', 'evidencia-secao');
    const h = el('div', 'evidencia-secao-cab');
    h.appendChild(el('p', 'cc-eyebrow', titulo));
    if (contagem !== undefined) h.appendChild(el('b', 'evidencia-conta', contagem));
    s.appendChild(h);
    return s;
  }

  function listaKnow(itens: KnowItem[]): HTMLElement {
    const ul = el('ul', 'evidencia-lista');
    for (const k of itens) {
      const li = el('li');
      li.appendChild(chip(k.st));
      li.appendChild(el('span', 'evidencia-texto', k.text));
      li.appendChild(el('span', 'evidencia-origem', k.origin === 'user' ? 'investigado (USER)' : 'BASE consolidada'));
      ul.appendChild(li);
    }
    return ul;
  }

  function tabelaTeto(destaque?: StatusClass): HTMLElement {
    const t = el('div', 'teto');
    t.appendChild(el('p', 'cc-eyebrow', 'Teto por evidencia · regra do banco (C-07)'));
    for (const linha of TETO) {
      const r = el('div', 'teto-linha');
      r.appendChild(el('span', 'teto-evidencia', linha.evidencia));
      const cl = el('span', 'teto-classes');
      for (const st of TODAS) {
        const c = el('span', 'ev-chip ev-mini ev-' + st + (linha.permite.includes(st) ? '' : ' ev-off') + (st === destaque ? ' ev-alvo' : ''), st);
        c.style.setProperty('--cor', COR_ST[st]);
        cl.appendChild(c);
      }
      r.appendChild(cl);
      t.appendChild(r);
    }
    t.appendChild(el('p', 'cc-nota', 'ND, E4 e TO-BE nao exigem evidencia porque nao afirmam nada sobre a realidade observada. A tela mostra o teto; o banco e quem recusa.'));
    return t;
  }

  function cadeia(inv: Investigation, no: FomNode): HTMLElement {
    const c = el('div', 'cadeia-v');
    const elo = (n: number, rotulo: string, corpo: HTMLElement, estado: 'on' | 'gap' | 'off'): void => {
      const st = el('div', 'cadeia-passo cadeia-passo-' + estado);
      st.appendChild(el('span', 'cadeia-ponto', String(n)));
      const b = el('div');
      b.appendChild(el('p', 'cc-eyebrow', rotulo));
      corpo.classList.add('cadeia-corpo');
      b.appendChild(corpo);
      st.appendChild(b);
      c.appendChild(st);
    };
    const p1 = el('div'); p1.appendChild(el('span', undefined, inv.origin_gap ?? 'pergunta aberta sem lacuna de origem declarada'));
    elo(1, 'lacuna', p1, inv.origin_gap ? 'on' : 'off');
    const p2 = el('div'); p2.appendChild(el('span', undefined, inv.question));
    const m2 = el('span', 'cadeia-meta', `${inv.code} · ${dataCurta(inv.created_at)}${inv.needed ? ' · evidencia necessaria: ' + inv.needed : ''}`);
    p2.appendChild(m2);
    elo(2, 'pergunta', p2, 'on');
    const p3 = el('div');
    if (inv.evidence.length) {
      const ul = el('ul', 'evidencia-lista');
      for (const e of inv.evidence) {
        const li = el('li');
        li.appendChild(el('span', 'evidencia-tipo', e.type + (e.partial ? ' · parcial' : ' · completa')));
        li.appendChild(el('span', 'evidencia-texto', (e.ref ? e.ref + ' · ' : '') + (e.description ?? '')));
        li.appendChild(el('span', 'cadeia-meta', (e.evidence_date ?? '—') + ' · registrada ' + dataCurta(e.created_at)));
        ul.appendChild(li);
      }
      p3.appendChild(ul);
    } else {
      p3.appendChild(el('span', undefined, 'nenhuma evidencia registrada — sem ela, so cabe ND, E4 ou TO-BE'));
    }
    elo(3, 'evidencia', p3, inv.evidence.length ? 'on' : 'gap');
    const p4 = el('div'); p4.appendChild(el('span', undefined, inv.answer_text ?? 'pendente (responder exige evidencia registrada)'));
    elo(4, 'resposta', p4, inv.answer_text ? 'on' : 'off');
    const p5 = el('div');
    if (inv.closing) {
      if (inv.closing.effect === 'log') p5.appendChild(el('span', undefined, `apenas registrado — o no manteve ${inv.closing.kept}. ${inv.closing.reason}`));
      else { p5.appendChild(chip(inv.closing.to)); p5.appendChild(el('span', undefined, ` ${inv.closing.effect === 'know' ? 'gerou conhecimento' : 'reclassificou o no'} (de ${inv.closing.from})`)); }
    } else p5.appendChild(el('span', undefined, 'pendente — so dentro do teto; o banco recusa acima dele'));
    elo(5, 'classificacao · com teto', p5, inv.closing ? 'on' : 'off');
    const p6 = el('div'); p6.appendChild(el('span', undefined, inv.status === 'encerrada'
      ? 'encerrada: o resultado vive no no e na trilha'
      : 'nasce ao encerrar (gerar conhecimento · reclassificar · apenas registrar)'));
    elo(6, 'conhecimento', p6, inv.status === 'encerrada' ? 'on' : 'off');
    const acoes = el('div', 'cc-acoes');
    acoes.appendChild(botao('Abrir esta investigacao', () => { fechar(); o.aoAbrirInvestigacoes(no); }, 'botao'));
    c.appendChild(acoes);
    return c;
  }

  async function abrir(no: FomNode, nos: FomNode[]): Promise<void> {
    noAtual = no;
    veu.hidden = false;
    veu.innerHTML = '';
    const folha = el('div', 'evidencia-folha');
    folha.setAttribute('role', 'dialog');
    folha.setAttribute('aria-modal', 'true');
    folha.setAttribute('aria-label', 'Evidence Inspector');
    veu.appendChild(folha);

    const cab = el('header', 'evidencia-cab');
    const t = el('div');
    t.appendChild(el('p', 'cc-eyebrow', 'Evidence Inspector'));
    t.appendChild(el('h2', 'evidencia-titulo', no.name));
    const linha = el('div', 'evidencia-chips');
    linha.appendChild(chip(no.st));
    if (no.node_type) linha.appendChild(el('span', 'etiqueta', rotuloTipo(no.node_type) ?? ''));
    linha.appendChild(el('span', 'etiqueta ' + (no.origin === 'base' ? 'etiqueta-base' : 'etiqueta-user'), no.origin === 'base' ? 'BASE congelada' : 'criado por voce'));
    if (ehFuturo(no)) linha.appendChild(el('span', 'cc-tag cc-tag-conceitual', 'TO-BE · intencao'));
    t.appendChild(linha);
    cab.appendChild(t);
    cab.appendChild(botao('Fechar · Esc', fechar, 'botao'));
    folha.appendChild(cab);

    folha.appendChild(el('p', 'cc-nota', DESCRICAO_ST[no.st]));

    /* fonte declarada = autoridade (texto livre no dominio) */
    const fonte = secao('Fonte declarada', no.src ? 'autoridade: texto livre' : '');
    fonte.appendChild(el('p', no.src ? 'evidencia-fonte' : 'evidencia-fonte evidencia-fonte-ausente',
      no.src ?? ((no.st === 'E1' || no.st === 'E2')
        ? 'Sem fonte declarada. Afirmacao E1/E2 sem referencia — heranca da BASE congelada; para nos criados aqui o dominio exige referencia.'
        : 'Sem fonte declarada.')));
    folha.appendChild(fonte);

    /* quatro perguntas */
    const sabemos = no.know.filter((k) => k.st === 'E1' || k.st === 'E2');
    const acreditamos = no.know.filter((k) => k.st === 'E3' || k.st === 'E4');
    const outros = no.know.filter((k) => !['E1', 'E2', 'E3', 'E4'].includes(k.st));
    const s1 = secao('Sabemos · E1 comprovado · E2 documental', String(sabemos.length));
    s1.appendChild(sabemos.length ? listaKnow(sabemos) : el('p', 'evidencia-vazio', 'Nada comprovado ou documentado neste objeto.'));
    folha.appendChild(s1);
    const s2 = secao('Acreditamos · E3 relato · E4 hipotese', String(acreditamos.length));
    s2.appendChild(acreditamos.length ? listaKnow(acreditamos) : el('p', 'evidencia-vazio', 'Nenhum relato ou hipotese registrado.'));
    folha.appendChild(s2);
    if (outros.length) { const sx = secao('Outros itens', String(outros.length)); sx.appendChild(listaKnow(outros)); folha.appendChild(sx); }
    const s3 = secao('Nao sabemos · lacunas', String(no.gaps.length));
    if (no.gaps.length) {
      const ul = el('ul', 'evidencia-lista');
      for (const g of no.gaps) {
        const li = el('li');
        li.appendChild(chip('ND'));
        li.appendChild(el('span', 'evidencia-texto', g));
        const escreve = o.papel() !== 'viewer';
        const b = botao('Investigar', () => { fechar(); o.aoInvestigarLacuna(no, g); }, 'botao botao-investigar');
        if (!escreve) { b.disabled = true; b.title = 'Disponivel para Editor ou Owner.'; }
        li.appendChild(b);
        ul.appendChild(li);
      }
      s3.appendChild(ul);
    } else s3.appendChild(el('p', 'evidencia-vazio', 'Nenhuma lacuna declarada.'));
    folha.appendChild(s3);
    const tobeDesc = nos.filter((n) => !n.removed_at && ehFuturo(n) && n.parent_external_id === no.external_id);
    const s4 = secao('Planejamos · TO-BE', String((ehFuturo(no) ? 1 : 0) + tobeDesc.length));
    if (ehFuturo(no)) s4.appendChild(el('p', 'evidencia-vazio', 'Este objeto e uma intencao futura declarada. NAO e evidencia de algo ja realizado.'));
    if (tobeDesc.length) {
      const ul = el('ul', 'evidencia-lista');
      for (const n of tobeDesc) { const li = el('li'); li.appendChild(chip('TOBE')); const b = el('button', 'evidencia-link', n.name); b.type = 'button'; b.addEventListener('click', () => { fechar(); o.aoIrParaNo(n.external_id); }); li.appendChild(b); ul.appendChild(li); }
      s4.appendChild(ul);
    }
    if (!ehFuturo(no) && !tobeDesc.length) s4.appendChild(el('p', 'evidencia-vazio', 'Nenhuma intencao futura ligada diretamente a este objeto.'));
    folha.appendChild(s4);

    /* investigacoes (PRIVATE) */
    const s5 = secao('Investigacoes deste objeto · cadeia epistemologica');
    folha.appendChild(s5);
    if (o.papel() === 'viewer') {
      s5.appendChild(el('p', 'evidencia-vazio', 'Investigacoes sao camada privada — disponiveis para Editor ou Owner. O mapa e o mesmo para todos; o que muda e a acao.'));
    } else {
      const carregando = el('p', 'evidencia-vazio', 'Lendo as investigacoes…');
      s5.appendChild(carregando);
      try {
        const lista: InvestigationResumo[] = await o.api.investigacoes(o.org(), o.mapa());
        if (noAtual !== no) return;
        const minhas = lista.filter((i) => i.node_external_id === no.external_id);
        carregando.remove();
        if (!minhas.length) {
          s5.appendChild(el('p', 'evidencia-vazio', 'Nenhuma investigacao aberta ou encerrada sobre este objeto. Investigar uma lacuna acima inicia a cadeia.'));
        } else {
          const abertas = minhas.filter((i) => i.status !== 'encerrada');
          const encerradas = minhas.filter((i) => i.status === 'encerrada');
          const detalhes = await Promise.all(minhas.slice(0, 6).map((i) => o.api.investigacao(o.org(), o.mapa(), i.code).catch(() => null)));
          if (noAtual !== no) return;
          const porCode = new Map(detalhes.filter((d): d is Investigation => !!d).map((d) => [d.code, d]));
          for (const grupo of [{ t: 'abertas', l: abertas }, { t: 'encerradas (historico)', l: encerradas }]) {
            if (!grupo.l.length) continue;
            const det = el('details', 'evidencia-grupo');
            if (grupo.t === 'abertas') det.open = true;
            const sum = el('summary', undefined, `${grupo.l.length} ${grupo.t}`);
            det.appendChild(sum);
            for (const i of grupo.l) {
              const d = porCode.get(i.code);
              if (d) det.appendChild(cadeia(d, no));
              else { const p = el('p', 'evidencia-vazio', `${i.code} · ${i.question} (detalhe nao carregado)`); det.appendChild(p); }
            }
            s5.appendChild(det);
          }
          if (minhas.length > 6) s5.appendChild(el('p', 'cc-nota', `Mostrando 6 de ${minhas.length}. Abra a camada de investigacoes para ver todas.`));
        }
      } catch {
        carregando.textContent = 'Nao foi possivel ler as investigacoes agora.';
      }
    }

    folha.appendChild(tabelaTeto(no.st));
    folha.appendChild(el('p', 'cc-moldura-agente', 'Agente: a saida do agente nao tem classe epistemica. Tem estado de proposta. Nunca aparece como conhecimento.'));
    folha.querySelector<HTMLButtonElement>('button')?.focus();
  }

  return { abrir, fechar, get aberta(): boolean { return !veu.hidden; } };
}
